import React, { useMemo } from "react";
import { useGoldenRatioMultiplierChart } from "@/hooks/coinglass/useGoldenRatioMultiplierChart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface GoldenRatioMultiplierChartProps {
  startDate?: number;
  endDate?: number;
  limit?: number;
  height?: string | number;
}

export const GoldenRatioMultiplierChart: React.FC<GoldenRatioMultiplierChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 500,
}) => {
  const { data, isLoading, isError, error } = useGoldenRatioMultiplierChart({
    start_timestamp: startDate,
    end_timestamp: endDate,
    limit,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data?.data) return null;

    const { xAxis, series } = data.data;

    // Colors for the bands
    const colors = [
      '#888',       // 350-Day MA
      '#10b981',    // Accumulation (x1.6)
      '#fbbf24',    // x3
      '#fbbf24',    // x5
      '#f97316',    // x8
      '#ef4444',    // x13
      '#dc2626',    // x21
      '#ffffff'     // BTC Price
    ];

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: "rgba(20, 20, 30, 0.9)",
        borderColor: "#333",
        textStyle: { color: "#fff" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          
          const p = params;
          const date = p[0].axisValue;
          
          // Filter out the area bands (which have empty names in our logic below)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const visibleParams = p.filter((item: any) => item.seriesName && item.seriesName !== '');

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const rows = visibleParams.map((item: any) => `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span style="color: #d1d5db; margin-right: 12px;">${item.seriesName}:</span>
              <span style="font-weight: bold; color: ${item.color};">$${typeof item.data === 'number' ? item.data.toLocaleString() : item.data}</span>
            </div>
          `).join('');

          return `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: #9ca3af;">${date}</div>
              ${rows}
            </div>
          `;
        }
      },
      legend: {
        data: series.map(s => s.name),
        textStyle: { color: "#9ca3af" },
        top: 0,
        left: "center",
        icon: "circle",
        type: "scroll",
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "12%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxis,
        axisLabel: {
          color: "#9ca3af",
          hideOverlap: true,
          formatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          },
        },
        axisLine: { lineStyle: { color: "#374151" } },
      },
      yAxis: {
        type: "log",
        logBase: 10,
        name: "Price (USD)",
        position: "right",
        splitLine: { lineStyle: { color: "#1f2937" } },
        axisLabel: {
          color: "#9ca3af",
          formatter: (value: number) => {
            if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
            if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
            if (value >= 1) return `$${value.toFixed(2)}`;
            return `$${value.toFixed(4)}`;
          },
        },
        scale: true,
      },
      series: series.map((s, index) => {
        const isPrice = index === series.length - 1;
        const color = colors[index] || '#fff';
        
        return {
          ...s,
          itemStyle: { color },
          lineStyle: { 
            width: isPrice ? 2 : 1,
            type: index === 0 ? 'dashed' : 'solid'
          },
          symbol: "none",
          z: isPrice ? 10 : 2
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
      dataZoom: [
        { type: "inside", start: 0, end: 100 },
        {
          type: "slider",
          show: true,
          bottom: 30,
          height: 20,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af" },
          brushSelect: false,
        },
      ],
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data?.data}
      containerClassName="w-full h-full"
    />
  );
};
